/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {QRecord} from "@temple-of-syrinx/qqq-frontend-core/lib/model/QRecord";
import React from "react";
import {Link, matchPath} from "react-router-dom";


export interface AuditLinkMaps
{
   byAuditId: Map<number, QRecord[]>;
   byAuditDetailId: Map<number, QRecord[]>;
}

interface InlineMatch
{
   link: QRecord;
   linkText: string;
   start: number;
}

/*******************************************************************************
 ** Make a pair of empty link maps, for a caller that has no links yet.
 **
 ** This is a factory rather than a shared constant on purpose - the maps are
 ** mutable, so a single shared instance could be written to by any consumer and
 ** stay poisoned for the rest of the process.
 *******************************************************************************/
export function makeEmptyAuditLinkMaps(): AuditLinkMaps
{
   return ({byAuditId: new Map(), byAuditDetailId: new Map()});
}


/*******************************************************************************
 ** Split auditLink records into the ones that belong to an audit and the ones
 ** that belong to one of its details, each keyed by the id they point at.
 *******************************************************************************/
export function buildAuditLinkMaps(links: QRecord[]): AuditLinkMaps
{
   const byAuditId: Map<number, QRecord[]> = new Map();
   const byAuditDetailId: Map<number, QRecord[]> = new Map();

   for (let i = 0; i < (links?.length ?? 0); i++)
   {
      const link = links[i];
      const auditDetailId = link.values.get("auditDetailId");
      const isDetailLink = (auditDetailId !== null && auditDetailId !== undefined);
      const map = isDetailLink ? byAuditDetailId : byAuditId;
      const key = isDetailLink ? auditDetailId : link.values.get("auditId");

      if (!map.has(key))
      {
         map.set(key, []);
      }

      map.get(key).push(link);
   }

   return ({byAuditId: byAuditId, byAuditDetailId: byAuditDetailId});
}


/*******************************************************************************
 ** An absolute http(s) url leaves the app; anything else is an in-app path.
 **
 ** The scheme is compared case-insensitively:  a url written HTTPS:// that fell
 ** through to the in-app branch would go on to the matchPath test in
 ** filterRoutableLinks, which no route pattern can satisfy, and the link would
 ** silently disappear - or, where there are no route patterns to test against,
 ** it would reach renderAnchor as a react-router Link, which renders any url it
 ** reads as absolute verbatim into a plain <a href>, so the link would leave the
 ** app in this tab, without the noopener/noreferrer the external branch sets.
 *******************************************************************************/
function checkIsExternalUrl(linkUrl: string): boolean
{
   const lowerCaseUrl = linkUrl.toLowerCase();
   return (lowerCaseUrl.startsWith("http://") || lowerCaseUrl.startsWith("https://"));
}


/*******************************************************************************
 ** Whether a url that is not external is safe to hand to react-router as a path.
 **
 ** linkUrl is text from the backend, and react-router's Link treats anything with
 ** a scheme, or a leading //, as absolute:  it writes that string into a raw
 ** <a href> and does not intercept the click, so a javascript: url would run on
 ** click and a //host one would leave the app.
 **
 ** A route of this app always begins with a single /, but being rooted there is
 ** not the whole bar, because the browser normalizes what it reads out of an href
 ** before resolving it:  it discards every ASCII tab and newline first, and then,
 ** against an http(s) document, reads a \ the same as a / - so /\evil.com and
 ** /<tab>/evil.com both resolve to https://evil.com/.  react-router reads neither
 ** as absolute, so each reaches the DOM as a path-looking href that cmd-click,
 ** middle-click or "Copy Link Address" follows off-site, without the
 ** noopener/noreferrer the external branch sets.  Hence the tabs and newlines come
 ** out before the test, and what follows the leading / has to be neither / nor \.
 *******************************************************************************/
function checkIsSafeInAppPath(linkUrl: string): boolean
{
   return (/^\/(?![/\\])/.test(linkUrl.replace(/[\t\n\r]/g, "")));
}


/*******************************************************************************
 ** Drop links whose in-app path has no generated route, or is not a path at all.
 **
 ** App.tsx ends its route list with a catch-all that navigates to the default
 ** route, so a link to a table the user has no route for would silently bounce
 ** them to the home page and destroy the audit modal on the way.  A table the
 ** user lacks permission to never had routes generated, so this is also the
 ** permission case.
 **
 ** pathToLabelMap is keyed by every generated route pattern, including
 ** parameterized ones, which is why the test is matchPath and not a lookup.
 *******************************************************************************/
export function filterRoutableLinks(links: QRecord[], pathToLabelMap: { [path: string]: string }): QRecord[]
{
   /////////////////////////////////////////////////////////////////////////////////
   // a link with no url can never render, so drop it before anything else.  That //
   // keeps the fail-open path below from handing one to renderAnchor, where      //
   // checkIsExternalUrl would throw mid-render and take out the audit modal.     //
   /////////////////////////////////////////////////////////////////////////////////
   const linksWithUrl = (links ?? []).filter((link) => !!link.values.get("linkUrl"));

   /////////////////////////////////////////////////////////////////////////////////
   // this runs ahead of the fail-open return below, not inside the route test,   //
   // because an unsafe url is never worth rendering - where a merely unroutable  //
   // one costs a redirect, while that one is a live javascript: or off-site href //
   /////////////////////////////////////////////////////////////////////////////////
   const safeLinks = linksWithUrl.filter((link) =>
   {
      const linkUrl = link.values.get("linkUrl");
      return (checkIsExternalUrl(linkUrl) || checkIsSafeInAppPath(linkUrl));
   });

   if (safeLinks.length === 0)
   {
      return ([]);
   }

   const routePatterns = Object.keys(pathToLabelMap ?? {});

   /////////////////////////////////////////////////////////////////////////////////
   // fail open:  with no patterns to test against, suppressing every internal    //
   // link would silently break the feature, where emitting one costs at most the //
   // redirect the router would do anyway                                         //
   /////////////////////////////////////////////////////////////////////////////////
   if (routePatterns.length === 0)
   {
      return (safeLinks);
   }

   return (safeLinks.filter((link) =>
   {
      const linkUrl = link.values.get("linkUrl");
      if (checkIsExternalUrl(linkUrl))
      {
         return (true);
      }

      const pathname = linkUrl.split("#")[0].split("?")[0];
      return (routePatterns.some((pattern) => matchPath(pattern, pathname) !== null));
   }));
}


/*******************************************************************************
 ** Render one link as an anchor.
 **
 ** An in-app path uses react-router's Link, which renders a real <a href> and
 ** only intercepts unmodified left-clicks, so command-click, middle-click and
 ** "Copy Link Address" all keep working.  The path is NOT run through
 ** resolveAssetUrl - react-router's useHref already prepends the basename from
 ** <BrowserRouter basename={detectBasePath()}>, so prefixing would double it.
 *******************************************************************************/
function renderAnchor(link: QRecord, children: React.ReactNode, key: string): JSX.Element
{
   const linkUrl = link.values.get("linkUrl");

   if (checkIsExternalUrl(linkUrl))
   {
      return (<a key={key} href={linkUrl} target="_blank" rel="noopener noreferrer">{children}</a>);
   }

   return (<Link key={key} to={linkUrl}>{children}</Link>);
}


/*******************************************************************************
 ** Whether a string holds anything a reader could see.
 **
 ** An anchor whose only content is whitespace is as invisible and unclickable as
 ** an empty one, so both a line's text and a link's own text have to clear this
 ** bar before either can become an anchor's content.
 *******************************************************************************/
function checkHasVisibleText(text: string): boolean
{
   return (!!text && text.trim().length > 0);
}


/*******************************************************************************
 ** The label to show for a link that is not anchoring text of its own.
 *******************************************************************************/
function makeLinkLabel(link: QRecord): string
{
   const linkText = link.values.get("linkText");
   return (checkHasVisibleText(linkText) ? linkText : "link");
}


/*******************************************************************************
 ** Render a line of audit text with its links applied.
 **
 ** content carries whether the line has text a link can name:  a string is the
 ** line's stored text and is matchable; an element is a line whose text is built
 ** at render time, and so has nothing to match against; and nullish is a line with
 ** no text at all - an audit's message is not a required field.  Passing one value
 ** rather than a (content, matchText) pair is what keeps those two from disagreeing
 ** - the matched branch below rebuilds the line from the text it matched in.
 **
 ** Rules:  a link whose text has something visible in it and occurs in that text
 ** anchors that substring (first occurrence, case-sensitive, overlaps skipped).
 ** Anything left over renders as a trailing anchor, in the order the links
 ** arrived - except that a single leftover with no inline matches anchors the
 ** whole line instead.  Anchors never nest.
 *******************************************************************************/
export function renderLinkedContent(content: string | JSX.Element | null | undefined, links: QRecord[], pathToLabelMap: { [path: string]: string }): JSX.Element
{
   const routableLinks = filterRoutableLinks(links, pathToLabelMap);
   if (routableLinks.length === 0)
   {
      return (<>{content}</>);
   }

   const matchText = (typeof content === "string") ? content : null;

   /////////////////////////////////////////////////////////////////////////////////////
   // find each link's first occurrence, if it has visible text and that text is here //
   /////////////////////////////////////////////////////////////////////////////////////
   const candidates: InlineMatch[] = [];
   for (let i = 0; i < routableLinks.length; i++)
   {
      const link = routableLinks[i];
      const linkText = link.values.get("linkText");
      const start = (matchText && checkHasVisibleText(linkText)) ? matchText.indexOf(linkText) : -1;
      if (start >= 0)
      {
         candidates.push({link: link, linkText: linkText, start: start});
      }
   }

   ///////////////////////////////////////////////////////////////////////////////////
   // accept matches left to right, skipping any that overlap one already accepted. //
   // This is also how two links with identical text resolve - the second overlaps  //
   // the first and falls through to a trailing anchor.                             //
   ///////////////////////////////////////////////////////////////////////////////////
   candidates.sort((a, b) => a.start - b.start);
   const matches: InlineMatch[] = [];
   const matchedLinks: Set<QRecord> = new Set();
   let acceptedThrough = 0;
   for (let i = 0; i < candidates.length; i++)
   {
      const candidate = candidates[i];
      if (candidate.start >= acceptedThrough)
      {
         matches.push(candidate);
         matchedLinks.add(candidate.link);
         acceptedThrough = candidate.start + candidate.linkText.length;
      }
   }

   /////////////////////////////////////////////////////////////////////////////////////
   // every link that did not anchor text inline trails the line, in the order the    //
   // links arrived.  That order is why this is a filter over the input rather than a //
   // list the loops above push their rejects onto - those two reject in a different  //
   // order than the links were given in.                                             //
   /////////////////////////////////////////////////////////////////////////////////////
   const leftover = routableLinks.filter((link) => !matchedLinks.has(link));

   //////////////////////////////////////////////////////////////////////////////////
   // nothing inline plus exactly one link => the whole line is that link.  A line //
   // with no visible text of its own would make an anchor with nothing in it -    //
   // invisible and unclickable - so fall back to the same label a trailing anchor //
   // would use.                                                                   //
   //////////////////////////////////////////////////////////////////////////////////
   if (matches.length === 0 && leftover.length === 1)
   {
      const hasText = (typeof content === "string") ? checkHasVisibleText(content) : (content !== null && content !== undefined);
      return (renderAnchor(leftover[0], hasText ? content : makeLinkLabel(leftover[0]), "whole"));
   }

   const parts: React.ReactNode[] = [];
   if (matches.length > 0)
   {
      let cursor = 0;
      for (let i = 0; i < matches.length; i++)
      {
         const match = matches[i];
         if (match.start > cursor)
         {
            parts.push(<React.Fragment key={`text${i}`}>{matchText.substring(cursor, match.start)}</React.Fragment>);
         }

         parts.push(renderAnchor(match.link, match.linkText, `match${i}`));
         cursor = match.start + match.linkText.length;
      }

      if (cursor < matchText.length)
      {
         parts.push(<React.Fragment key="textEnd">{matchText.substring(cursor)}</React.Fragment>);
      }
   }
   else
   {
      parts.push(<React.Fragment key="content">{content}</React.Fragment>);
   }

   for (let i = 0; i < leftover.length; i++)
   {
      const link = leftover[i];
      parts.push(<React.Fragment key={`trailWrap${i}`}> {renderAnchor(link, makeLinkLabel(link), `trail${i}`)}</React.Fragment>);
   }

   return (<>{parts}</>);
}
